import { ApiSettings } from '../../../core/config/api-settings';

export type CodeLanguage = 'ruby' | 'typescript' | 'curl';

export const CODE_LANGUAGES: { id: CodeLanguage; label: string }[] = [
  { id: 'ruby', label: 'Ruby' },
  { id: 'typescript', label: 'TypeScript' },
  { id: 'curl', label: 'cURL' },
];

function indent(level: number): string {
  return '  '.repeat(level);
}

function toCamelCase(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

function formatRubyValue(value: any, level = 0): string {
  if (value === null || value === undefined) return 'nil';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map(v => `${indent(level + 1)}${formatRubyValue(v, level + 1)}`).join(',\n');
    return `[\n${items}\n${indent(level)}]`;
  }

  const entries = Object.entries(value);
  if (entries.length === 0) return '{}';
  const lines = entries.map(([k, v]) => `${indent(level + 1)}${k}: ${formatRubyValue(v, level + 1)}`).join(',\n');
  return `{\n${lines}\n${indent(level)}}`;
}

function formatTsValue(value: any, level = 0): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return `'${value.replace(/'/g, "\\'")}'`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map(v => `${indent(level + 1)}${formatTsValue(v, level + 1)}`).join(',\n');
    return `[\n${items}\n${indent(level)}]`;
  }

  const entries = Object.entries(value);
  if (entries.length === 0) return '{}';
  const lines = entries.map(([k, v]) => `${indent(level + 1)}${toCamelCase(k)}: ${formatTsValue(v, level + 1)}`).join(',\n');
  return `{\n${lines}\n${indent(level)}}`;
}

function buildRubySnippet(method: string, payload: object): string {
  const entries = Object.entries(payload);
  const args = entries.length
    ? `\n${entries.map(([k, v]) => `${indent(1)}${k}: ${formatRubyValue(v, 1)}`).join(',\n')}\n`
    : '';

  return [
    "require 'mila'",
    '',
    'client = Mila::Client.new(api_key: "YOUR_API_KEY")',
    '',
    // Named `result`, not `response` the call itself now takes a
    // `response:` argument (the "Response configuration" group), so
    // `response = client.rag(..., response: {...})` would read as if the
    // variable and the argument were the same thing.
    `result = client.${method}(${args})`,
    '',
    'puts result',
  ].join('\n');
}

function buildTypeScriptSnippet(method: string, payload: object): string {
  return [
    "import { MilaClient } from 'mila';",
    '',
    "const client = new MilaClient({ apiKey: 'YOUR_API_KEY' });",
    '',
    // Same reasoning as the Ruby snippet `result`, not `response`, to stay
    // distinct from the `response:` argument key.
    `const result = await client.${method}(${formatTsValue(payload)});`,
    '',
    'console.log(result);',
  ].join('\n');
}

function buildCurlSnippet(endpoint: string, payload: object): string {
  // ApiSettings.baseUrl already carries the right host per build config
  // (local api.mila.test:3000, hosted dev.search-mila.ai, ...) and ends in
  // a trailing slash, so this needs no separating "/" of its own.
  return [
    `curl -X POST ${ApiSettings.baseUrl}${endpoint} \\`,
    '  -H "Authorization: Bearer YOUR_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(payload, null, 2)}'`,
  ].join('\n');
}

export function buildCodeSnippets(method: string, endpoint: string, payload: object): Record<CodeLanguage, string> {
  return {
    ruby: buildRubySnippet(method, payload),
    typescript: buildTypeScriptSnippet(method, payload),
    curl: buildCurlSnippet(endpoint, payload),
  };
}
